import { buildApiUrl, withBusinessHeaders } from './apiConfig';

const PURCHASE_ORDERS_API_URL =
  process.env.REACT_APP_PURCHASE_ORDERS_API_URL ||
  buildApiUrl('/purchase-orders');

export async function fetchPurchaseOrders() {
  const response = await requestPurchaseOrderApi(PURCHASE_ORDERS_API_URL, {
    fallbackMessage: 'Unable to load purchase orders right now.',
  });

  return response.json();
}

export async function createPurchaseOrder(payload) {
  const response = await requestPurchaseOrderApi(PURCHASE_ORDERS_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to save purchase order right now.',
  });

  return response.json();
}

export async function fetchInitialSummaryPurchaseOrders() {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/initial-summaries`,
    {
      fallbackMessage: 'Unable to load Initial Summary P.O. statuses right now.',
    }
  );

  return response.json();
}

export async function fetchSupplierBalancings() {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-balancings`,
    {
      fallbackMessage: 'Unable to load supplier balancing records right now.',
    }
  );

  return response.json();
}

export async function fetchSupplierSavingsReleases() {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-savings-releases`,
    {
      fallbackMessage: 'Unable to load supplier savings releases right now.',
    }
  );

  return response.json();
}

export async function fetchInitialSummaryPurchaseOrder(poDate) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/initial-summary?poDate=${encodeURIComponent(
      poDate || ''
    )}`,
    {
      fallbackMessage: 'Unable to load Initial Summary P.O. right now.',
    }
  );

  return response.json();
}

export async function saveInitialSummaryPurchaseOrder(payload) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/initial-summary`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      fallbackMessage: 'Unable to save Initial Summary P.O. right now.',
    }
  );

  return response.json();
}

export async function deleteInitialSummaryPurchaseOrder(poDate) {
  await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/initial-summary?poDate=${encodeURIComponent(
      poDate || ''
    )}`,
    {
      method: 'DELETE',
      fallbackMessage: 'Unable to clear Initial Summary P.O. right now.',
    }
  );
}

export async function updatePurchaseOrder(id, payload) {
  const response = await requestPurchaseOrderApi(`${PURCHASE_ORDERS_API_URL}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update purchase order right now.',
  });

  return response.json();
}

export async function updatePurchaseOrderProgress(id, payload) {
  const response = await requestPurchaseOrderApi(`${PURCHASE_ORDERS_API_URL}/${id}/progress`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update purchase order progress right now.',
  });

  return response.json();
}

export async function finalizePurchaseOrderForBalancing(id) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/${id}/balancing-ready`,
    {
      method: 'PATCH',
      fallbackMessage: 'Unable to finalize purchase order for balancing right now.',
    }
  );

  return response.json();
}

export async function reopenPurchaseOrderReceivingForBalancing(id) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/${id}/balancing-reopen`,
    {
      method: 'PATCH',
      fallbackMessage: 'Unable to reopen receiving details right now.',
    }
  );

  return response.json();
}

export async function createPurchaseOrderMerge(payload) {
  const response = await requestPurchaseOrderApi(`${PURCHASE_ORDERS_API_URL}/merges`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to merge purchase orders right now.',
  });

  return response.json();
}

export async function createSupplierBalancing(payload) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-balancings`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      fallbackMessage: 'Unable to save supplier balancing right now.',
    }
  );

  return response.json();
}

export async function createSupplierSavingsRelease(payload) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-savings-releases`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      fallbackMessage: 'Unable to release supplier savings right now.',
    }
  );

  return response.json();
}

export async function reverseSupplierSavingsRelease(payload) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-savings-releases/reversals`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      fallbackMessage: 'Unable to reverse supplier savings release right now.',
    }
  );

  return response.json();
}

export async function updateSupplierBalancingStatus(id, status) {
  const response = await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-balancings/${id}/status`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
      fallbackMessage: 'Unable to update supplier balancing status right now.',
    }
  );

  return response.json();
}

export async function deleteSupplierBalancing(id) {
  await requestPurchaseOrderApi(
    `${PURCHASE_ORDERS_API_URL}/supplier-balancings/${id}`,
    {
      method: 'DELETE',
      fallbackMessage: 'Unable to edit supplier balancing right now.',
    }
  );
}

export async function fetchPurchaseOrderHistory(id) {
  const response = await requestPurchaseOrderApi(`${PURCHASE_ORDERS_API_URL}/${id}/history`, {
    fallbackMessage: 'Unable to load purchase order history right now.',
  });

  return response.json();
}

async function requestPurchaseOrderApi(url, options) {
  const { fallbackMessage, ...requestOptions } = options;
  let response;

  try {
    response = await fetch(url, {
      ...requestOptions,
      headers: withBusinessHeaders(requestOptions.headers),
    });
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error, fallbackMessage));
  }

  if (!response.ok) {
    const errorPayload = await safeReadJson(response);
    throw new Error(errorPayload?.message || fallbackMessage);
  }

  return response;
}

function getNetworkErrorMessage(error, fallbackMessage) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You are offline right now. Please reconnect to continue.';
  }

  if (error instanceof TypeError) {
    return 'Unable to reach the server right now. Please check your connection or try again in a moment.';
  }

  return fallbackMessage;
}

async function safeReadJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}
